/**
 * Sign-up form behaviour.
 *
 * - Audience toggle reveals the matching field set; inactive sets are disabled
 *   so their fields aren't submitted or validated.
 * - Client-side validation mirrors the server (which stays authoritative).
 * - Accessible errors: aria-invalid + per-field message, a role="alert" summary,
 *   and focus moved to the first error.
 * - reCAPTCHA v3 token fetched when a site key is present (no key → empty token,
 *   server uses the mock verifier).
 * - Success → accessible modal dialog.
 */
export function initForm() {
  const root = document.querySelector('[data-emc-form]');
  if (!root) return;

  const sets = Array.from(root.querySelectorAll('[data-emc-set]'));
  const audienceRadios = Array.from(root.querySelectorAll('[data-emc-audience]'));
  const status = root.querySelector('.emc-form__status');
  const actions = root.querySelector('.emc-form__actions');
  const container = root.closest('.emc-form');
  const modal = container?.querySelector('[data-emc-modal]');

  const setSetEnabled = (set, enabled) => {
    set.hidden = !enabled;
    set.querySelectorAll('input, select').forEach((el) => {
      el.disabled = !enabled;
    });
  };

  // Figma: the compact "Stay Connected" state shows only through the audience
  // question — the submit actions appear once an audience is chosen. Choosing an
  // audience also collapses the "Who's registering today?" prompt (is-audience-
  // selected on the container). The "* required" note lives inside each set.
  const setActionsVisible = (visible) => {
    if (actions) actions.hidden = !visible;
    container?.classList.toggle('is-audience-selected', visible);
  };

  // Start with nothing revealed; disable all sets and hide the actions.
  sets.forEach((set) => setSetEnabled(set, false));
  setActionsVisible(false);

  const currentAudience = () =>
    audienceRadios.find((r) => r.checked)?.value || '';

  audienceRadios.forEach((radio) => {
    radio.addEventListener('change', () => {
      const audience = currentAudience();
      sets.forEach((set) =>
        setSetEnabled(set, set.dataset.emcSet === audience)
      );
      setActionsVisible(!!audience);
      clearErrors();
    });
  });

  // Cancel (type="reset") returns the form to its initial state: clear the
  // audience radios, collapse the field sets + actions, and scroll back up to
  // the top of the form ("Stay connected with us"). Runs after the browser's
  // native reset has cleared the inputs (hence the microtask defer).
  const collapse = () => {
    audienceRadios.forEach((r) => {
      r.checked = false;
    });
    sets.forEach((set) => setSetEnabled(set, false));
    setActionsVisible(false);
    clearErrors();

    // Scroll the form heading back into view (honouring reduced-motion).
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    (container || root).scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  root.addEventListener('reset', () => {
    Promise.resolve().then(collapse);
  });

  // Clear a control's invalid (red) state as soon as the user interacts with it.
  // "Choose at least one" checkbox groups (specialty, contact method) are
  // satisfied as a whole once any box is checked, so clear the entire group.
  // Consents are each individually required, so only clear the box the user just
  // toggled — checking one must not clear the red on the others.
  root.addEventListener('change', (e) => {
    const el = e.target;
    if (!(el instanceof HTMLElement) || !el.matches('input, select')) return;
    el.setAttribute('aria-invalid', 'false');
    if (
      el.type === 'checkbox' &&
      el.name.endsWith('[]') &&
      el.name !== 'consents[]'
    ) {
      root
        .querySelectorAll(`input[name="${el.name}"]`)
        .forEach((c) => c.setAttribute('aria-invalid', 'false'));
    }
  });

  function clearErrors() {
    root
      .querySelectorAll('[aria-invalid="true"]')
      .forEach((el) => el.setAttribute('aria-invalid', 'false'));
    root
      .querySelectorAll('.emc-field__error')
      .forEach((el) => (el.textContent = ''));
    if (status) {
      status.hidden = true;
      status.textContent = '';
    }
  }

  function fieldError(input, message) {
    input.setAttribute('aria-invalid', 'true');
    const describedby = input.getAttribute('aria-describedby');
    const node = describedby && document.getElementById(describedby);
    if (node) node.textContent = message;
  }

  // Single form-level message near the submit button (no top summary). Used for
  // group/submit-level failures (audience, specialty, consent, server, network).
  function showStatus(message) {
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    status.focus();
  }

  function validate(activeSet) {
    // Field-level messages are shown inline under each field; group-level ones
    // (specialty, contact method, consents) surface as a single status message.
    const groupMessages = [];
    let firstInvalid = null;
    let groupInvalid = false;

    // Required text/select fields in the active set (inline errors).
    activeSet
      .querySelectorAll('input[required], select[required]')
      .forEach((el) => {
        if (el.type === 'checkbox') return;
        const value = (el.value || '').trim();
        let msg = '';
        if (!value) {
          msg = 'This field is required.';
        } else if (el.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          msg = 'Please enter a valid email address.';
        } else if (el.name === 'zip' && !/^\d{5}(-\d{4})?$/.test(value)) {
          msg = 'Please enter a valid ZIP code.';
        }
        if (msg) {
          fieldError(el, msg);
          firstInvalid = firstInvalid || el;
        }
      });

    // HCP specialty: at least one (flag every box so the group shows red).
    const specialty = Array.from(activeSet.querySelectorAll('input[name="specialty[]"]'));
    if (specialty.length && !specialty.some((c) => c.checked)) {
      specialty.forEach((c) => c.setAttribute('aria-invalid', 'true'));
      firstInvalid = firstInvalid || specialty[0];
      groupInvalid = true;
    }

    // HCP preferred contact method: at least one.
    const contactMethod = Array.from(activeSet.querySelectorAll('input[name="contact_method[]"]'));
    if (contactMethod.length && !contactMethod.some((c) => c.checked)) {
      contactMethod.forEach((c) => c.setAttribute('aria-invalid', 'true'));
      firstInvalid = firstInvalid || contactMethod[0];
      groupInvalid = true;
    }

    // Required consents — flag each unchecked one so its checkbox shows red.
    const requiredConsents = Array.from(
      activeSet.querySelectorAll('input[name="consents[]"][required]')
    );
    requiredConsents.forEach((c) => {
      if (!c.checked) {
        c.setAttribute('aria-invalid', 'true');
        firstInvalid = firstInvalid || c;
        groupInvalid = true;
      }
    });

    if (groupInvalid) {
      groupMessages.push('Please complete all required fields to continue.');
    }

    return { firstInvalid, groupMessages };
  }

  // Fetch a fresh nonce from the server — always live, never from the page HTML.
  // Throws on failure so the submit handler can surface the error rather than
  // sending a dead nonce and getting a 401.
  async function freshNonce() {
    const nonceUrl = root.getAttribute('action').replace(/\/signup$/, '/nonce');
    const res = await fetch(nonceUrl, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('nonce fetch failed');
    const data = await res.json();
    if (!data.nonce) throw new Error('nonce missing');
    return data.nonce;
  }

  async function captchaToken() {
    const siteKey = root.dataset.recaptchaSite;
    if (!siteKey || typeof window.grecaptcha === 'undefined') return '';
    try {
      await new Promise((res) => window.grecaptcha.ready(res));
      return await window.grecaptcha.execute(siteKey, { action: 'signup' });
    } catch (e) {
      return '';
    }
  }

  function openModal() {
    if (!modal) return;
    modal.hidden = false;
    const close = modal.querySelector('[data-emc-modal-close]');
    close?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    modal.addEventListener('keydown', onKey);
    close?.addEventListener('click', closeModal, { once: true });
    function closeModal() {
      modal.hidden = true;
      modal.removeEventListener('keydown', onKey);
    }
  }

  root.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearErrors();

    const audience = currentAudience();
    if (!audience) {
      const first = audienceRadios[0];
      first?.focus();
      showStatus('Please choose whether you are a Healthcare Professional or Patient/Caregiver.');
      return;
    }
    const activeSet = sets.find((s) => s.dataset.emcSet === audience);
    if (!activeSet) return;

    const { firstInvalid, groupMessages } = validate(activeSet);
    if (firstInvalid || groupMessages.length) {
      // Inline errors are already on the fields; focus the first invalid one.
      // Group-level messages (specialty/consent) surface near the submit button.
      if (groupMessages.length) {
        showStatus(groupMessages.join(' '));
      }
      firstInvalid?.focus();
      return;
    }

    const submitBtn = root.querySelector('.emc-form__submit');
    if (submitBtn) submitBtn.disabled = true;

    // Fill captcha token and fetch a fresh nonce (bypasses page cache).
    const tokenInput = root.querySelector('[data-emc-captcha]');
    const [captcha, nonce] = await Promise.all([captchaToken(), freshNonce()]);
    if (tokenInput) tokenInput.value = captcha;

    try {
      const res = await fetch(root.getAttribute('action'), {
        method: 'POST',
        headers: { 'X-EMC-Nonce': nonce },
        body: new FormData(root),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.success) {
        root.dispatchEvent(
          new CustomEvent('emc:form-success', {
            detail: { audience, delayed: !!data.delayed },
            bubbles: true,
          })
        );
        root.reset();
        sets.forEach((set) => setSetEnabled(set, false));
        setActionsVisible(false);
        openModal();
      } else if (res.status === 422 && data.errors) {
        // Server is authoritative; mirror its field errors inline where possible.
        let firstServerInvalid = null;
        Object.entries(data.errors).forEach(([name, msg]) => {
          const input = root.querySelector(
            `[name="${name}"], [name="${name}[]"]`
          );
          if (input) {
            fieldError(input, msg);
            firstServerInvalid = firstServerInvalid || input;
          }
        });
        if (firstServerInvalid) {
          firstServerInvalid.focus();
        } else {
          showStatus('Please review your entries and try again.');
        }
        root.dispatchEvent(
          new CustomEvent('emc:form-error', {
            detail: { type: 'validation' },
            bubbles: true,
          })
        );
      } else {
        showStatus(data.message || 'Something went wrong. Please try again.');
        root.dispatchEvent(
          new CustomEvent('emc:form-error', {
            detail: { type: 'server', status: res.status },
            bubbles: true,
          })
        );
      }
    } catch (err) {
      showStatus('We could not submit the form. Please check your connection and try again.');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });
}
